/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file auth-selector.js
 * @description Redux selectors for accessing auth state data
 */

import { createSelector } from "@reduxjs/toolkit";

/**
 * @function getAuthSelector
 * @description Selector that returns the auth data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthSelector = (state) => state.auth || {};

/**
 * @function getAuthAccountId
 * @description Selector that returns the auth account id data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthAccountId = (state) => state.auth?.accountId;

/**
 * @function getAuthCurrencyCode
 * @description Selector that returns the auth currency code data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthCurrencyCode = (state) => state.auth?.currencyCode;

/**
 * @function getAuthError
 * @description Selector that returns the auth error data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthError = (state) => state.auth?.error;

/**
 * @function getAuthDesktopView
 * @description Selector that returns the auth desktop view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
// View based on CMS data...
export const getAuthDesktopView = (state) => state.auth?.desktopView;

/**
 * @function getAuthLanguage
 * @description Selector that returns the auth language data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLanguage = (state) => state.auth?.language;

/**
 * @function getAuthLoading
 * @description Selector that returns the auth loading data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLoading = (state) => state.auth?.loading;

/**
 * @function getAuthLoggedIn
 * @description Selector that returns the auth logged in data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLoggedIn = (state) => !!state.auth?.loggedIn;

/**
 * @function getAuthMobileView
 * @description Selector that returns the auth mobile view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
// View based on CMS data...
export const getAuthMobileView = (state) => state.auth?.mobileView;

/**
 * @function getAuthMobileTheme
 * @description Selector that returns the auth mobile theme data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthMobileTheme = (state) => state.auth?.mobileTheme;

/**
 * @function getAuthPriceFormat
 * @description Selector that returns the auth price format data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthPriceFormat = (state) => state.auth?.priceFormat;

/**
 * @function getAuthRememberedUsername
 * @description Selector that returns the auth remembered username data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthRememberedUsername = (state) => state.auth?.rememberedUsername;

/**
 * @function getAuthTimezoneOffset
 * @description Selector that returns the auth timezone offset data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthTimezoneOffset = (state) => state.auth?.timezoneOffset;

/**
 * @function getAuthToken
 * @description Selector that returns the auth token data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthToken = (state) => state.auth?.authToken;

/**
 * @function getAuthUsername
 * @description Selector that returns the auth username data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthUsername = (state) => state.auth?.username;

/**
 * @function getAuthIsIframe
 * @description Selector that returns the auth is iframe data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthIsIframe = (state) => state.auth?.isIframe;

/**
 * @function getAuthLoginURL
 * @description Selector that returns the auth login u r l data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLoginURL = (state) => state.auth?.authLoginURL;

/**
 * @function getAuthReferrer
 * @description Selector that returns the referrer URL data from state or document.referrer if state value not set
 * @param {Object} state - Redux state
 * @returns {string|null} The referrer URL, document.referrer as fallback, or null if not set
 */
export const getAuthReferrer = createSelector(
  [(state) => state.auth?.referrer, () => (typeof document !== "undefined" ? document.referrer : null)],
  (stateReferrer, documentReferrer) => stateReferrer || documentReferrer || null,
);

/**
 * @function getAuthIsSplitModePreferred
 * @description Selector that returns the auth is split mode preferred data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthIsSplitModePreferred = (state) => state.auth?.isSplitModePreferred;

/**
 * @function getAuthTill
 * @description Selector that returns the auth till data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthTill = (state) => state.auth?.tillAuth;

/**
 * @function getAuthIFrameQueryParamsProcessed
 * @description Selector that returns the auth i frame query params processed data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthIFrameQueryParamsProcessed = (state) => !!state.auth?.authIFrameQueryParamsProcessed;

/**
 * @function getAuthPlayerRequestedDesktopView
 * @description Selector that returns the auth player requested desktop view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
// View based on CMS data...
export const getAuthPlayerRequestedDesktopView = (state) => state.auth?.playerRequestedDesktopView;

/**
 * @function getAuthPlayerRequestedMobileView
 * @description Selector that returns the auth player requested mobile view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
// View based on CMS data...
export const getAuthPlayerRequestedMobileView = (state) => state.auth?.playerRequestedMobileView;

/**
 * @function getAuthPlayerRequestedPriceFormat
 * @description Selector that returns the auth player requested price format data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
// View based on CMS data...
export const getAuthPlayerRequestedPriceFormat = (state) => state.auth?.playerRequestedPriceFormat;
